import {
  Component, OnInit, OnDestroy, AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule }               from '@angular/forms';
import { NgApexchartsModule }        from 'ng-apexcharts';
import { NgbModule }                 from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil, forkJoin } from 'rxjs';

import { LogSelectComponent }        from '../../../shared/component/log-select/log-select.component';
import { ApiCallsService }           from '../../../shared/services/api-calls.service';
import { ReportPaginationService }   from '../Reports/report-pagination.service';
import { CriticalAlarmsPaginationService } from './critical-alarms-pagination.service';

export interface AlarmCategoryList {
  GRID:  string[];
  DG:    string[];
  OTHER: string[];
}
// declare var bootstrap: any;
import {
  buildStatCardsFromCountRows,
  buildDeviceStatsFromCountRows,
  buildTypeBarsFromCountRows,
  buildPieDataFromCountRows,
  CountRow,
  buildTrendChartFromTimeRows,
  AlarmTimeRow,
  initAlarmCategories,
  buildTrendChartFromTimeRowsProgressive,
} from './alarm-counts-mapper';
import { TableComponent } from "../../../shared/component/table/table.component";
import { ModalsPaginationService } from '../../../shared/component/table-Modal/Modal-pagination.service';

// ── Types ─────────────────────────────────────────────────────────────────

export interface AlarmType {
  label: string;
  count: number;
  cat:   'grid' | 'dg' | 'others';
  sev:   'high' | 'med' | 'low';
}

export interface ApiHeader {
  name:   string;
  key:    string;
  filter: boolean;
  show:   boolean;
  click:  boolean;
}

export interface PieCategory { label: string; color: string; }
export interface DeviceStat  { name: string; color: string; count: number; pct: number; }

export type AlarmPriority = 'p1' | 'p2';

export interface CriticalAlarm {
  type: string; device: string; devId: string;
  name: string; data:   string; time:  string;
  priority: AlarmPriority;_raw:any
}

export function alarmPriorityFromSeverity(severity: string | null | undefined): AlarmPriority {
  const s = (severity ?? '').trim().toLowerCase();
  if (s === 'high' || s === 'p1') return 'p1';
  return 'p2';
}

// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-alarmsdashboard',
  standalone: true,
  imports: [
    LogSelectComponent, CommonModule, DecimalPipe,
    NgApexchartsModule, NgbModule, FormsModule,
    TableComponent
  ],
  templateUrl: './alarm-dashboard.component.html',
  styleUrls:   ['./alarm-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlarmsDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  showReport:boolean = false
  constructor(
    private alarmApi:        ApiCallsService,
    private cdr:             ChangeDetectorRef,
    private reportService:   ReportPaginationService,
    private criticalService: CriticalAlarmsPaginationService,
    private modalService:    ModalsPaginationService
  ) {}

  // ── Teardown ──────────────────────────────────────────────────────────────
  private destroy$ = new Subject<void>();

  // ── UI state ──────────────────────────────────────────────────────────────
  isLoading     = false;
  showDashboard = false;
  apiError      = '';
  deviceStatsOpen = false;

  isTrendChartLoading = false;        // ← ADD THIS
  trendChartLoadingProgress = 0;      // ← ADD THIS

  // ── Query state ───────────────────────────────────────────────────────────
  private currentQuery: any;
  private fromtime = '';
  private totime   = '';

  // ── Data stores  (kept separate — different shapes) ───────────────────────
  private countRows:  CountRow[]    = [];   // from ALARM_COUNT
  private trendRows:  AlarmTimeRow[] = [];  // from TREND_ALARMS  ← NEW separate store

  // ── Derived UI data ───────────────────────────────────────────────────────
  statCards:   any[]       = [];
  deviceStats: DeviceStat[] = [];
  private deviceColourMap  = new Map<string, string>();
  private alarmTypeBars: AlarmType[] = [];

  // ── Chart options ─────────────────────────────────────────────────────────
  trendChartOptions:  any = { series: [], chart: { type: 'line',  height: '100%', width: '100%' } };
  pieChartOptions:    any = { series: [], chart: { type: 'donut', height: '100%', width: '100%' } };
  deviceChartOptions: any = { series: [], chart: { type: 'bar',   height: '100%', width: '100%' } };

  // ── Shared constants ──────────────────────────────────────────────────────
  readonly sevColors: Record<string, string> = {
    high: '#ef4444', med: '#f59e0b', low: '#22c55e',
  };

  readonly pieCategories: PieCategory[] = [
    { label: 'No response', color: '#ef4444' },
    { label: 'Grid alarms', color: '#3b82f6' },
    { label: 'DG alarms',   color: '#f59e0b' },
    { label: 'Low PF',      color: '#dc2626' },
    { label: 'Timeout',     color: '#22c55e' },
    { label: 'Maintenance', color: '#a78bfa' },
    { label: 'Other',       color: '#6b7280' },
  ];

  // ── Hbar filter ───────────────────────────────────────────────────────────
  hbarFilter: 'all' | 'grid' | 'dg' | 'others' = 'all';

  readonly hbarFilters = [
    { key: 'all'    as const, label: 'All'    },
    { key: 'grid'   as const, label: 'Grid'   },
    { key: 'dg'     as const, label: 'DG'     },
    { key: 'others' as const, label: 'Others' },
  ];

  get filteredBars(): AlarmType[] {
    const data = this.hbarFilter === 'all'
      ? this.alarmTypeBars
      : this.alarmTypeBars.filter(a => a.cat === this.hbarFilter);
    return [...data].sort((a, b) => b.count - a.count).slice(0, 8);
  }

  get maxBarCount(): number { return this.filteredBars[0]?.count ?? 1; }

  // ── Recent alarms table ───────────────────────────────────────────────────
  tableFilter:      'all' | 'high' | 'med' | 'low' = 'all';
  tableCurrentPage  = 1;
  readonly tablePageSize = 10;

  readonly tableFilters = [
    { key: 'all'  as const, label: 'All'      },
    { key: 'high' as const, label: 'Critical' },
    { key: 'med'  as const, label: 'Warning'  },
    { key: 'low'  as const, label: 'Info'     },
  ];

  // ── Critical accordion ────────────────────────────────────────────────────
  criticalOpen         = true;
  criticalSearch       = '';
  criticalSevFilter:   'all' | 'p1' | 'p2' = 'all';
  criticalDeviceFilter = '';
  criticalPage         = 1;
  readonly criticalPageSize = 8;

  readonly criticalSevFilters = [
    { key: 'all' as const, label: 'All critical' },
    { key: 'p1'  as const, label: 'P1 — Urgent'  },
    { key: 'p2'  as const, label: 'P2 — High'    },
  ];

  pagedCritical: CriticalAlarm[] = [];

  FILTER_CONFIG = [
    {
      label: '', type: 'text', apiKey: 'search',
      placeholder: 'Device Name',
      col: "col-12 col-sm-12 col-md-4 col-lg-4 col-xl-4", value: ''
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────────────────
  totalCount = 0;
  criticalHeaders: ApiHeader[] = [];

  ngOnInit(): void {
    this.reportService.showTable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => { this.showDashboard = v; this.cdr.markForCheck(); });  // ← add markForCheck

    this.reportService.totalCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(c => { this.totalCount = c; this.cdr.markForCheck(); });     // ← add markForCheck

    this.criticalService.page$
      .pipe(takeUntil(this.destroy$))
      .subscribe(rows => { this.pagedCritical = rows; this.cdr.markForCheck(); });

    this.criticalService.totalCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(n => { this.criticalTotalCount = n; this.cdr.markForCheck(); });

    this.criticalService.isLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => { this.isCriticalLoading = v; this.cdr.markForCheck(); });

    this.criticalService.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe(e => { this.criticalError = e; this.cdr.markForCheck(); });

    this.criticalService.headers$
      .pipe(takeUntil(this.destroy$))
      .subscribe(h => { this.criticalHeaders = h; this.cdr.markForCheck(); }); // ← was missing markForCheck
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handlePageChange(event: { page: number; pageSize: number }) {
    this.reportService.changePage(event, this.destroy$);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Main entry point — called by log-select
  // ─────────────────────────────────────────────────────────────────────────

  handleFilters(payload: Record<string, any> = {}): void {
    this.showReport = true
    this.resetDashboard()
    this.currentQuery = { ...payload };
    this.fromtime     = payload['from_date'] ?? '';
    this.totime       = payload['to_date']   ?? '';

    this.reportService.initFilters(
      this.currentQuery, 'ALARMS_REPORT', this.destroy$, 'Alarms Logs',
    );

    this.fetchAlarmIdList(this.currentQuery);
    // Fire critical alarms independently (it has its own pagination service)
    this.fetchCriticalAlarms(this.currentQuery);

    // ── Step 1: fetch category list first, THEN fetch counts + trend ─────
    // AlarmIdList seeds initAlarmCategories() — counts must not run before it.
  }

  /** Called by <app-log-select> (showTable output) */

  // ─────────────────────────────────────────────────────────────────────────
  // API calls — sequenced correctly
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Step 1 — seed GRID / DG sets from the category list API.
   * Only after this resolves do we fire the counts + trend calls.
   */
  private fetchAlarmIdList(query: any): void {
    const payload = {
      site:     query.site_ids,
      fromdate: query.from_date,
      todate:   query.to_date,
      type:     'ALARM_ID_LIST',
      search:   query.search
    };

    this.alarmApi.GetAlarmsCounts_Critical(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          // Seed the dynamic GRID / DG sets in the mapper
          initAlarmCategories(data.Data.list as AlarmCategoryList);

          // Step 2 — now safe to fire counts + trend in parallel
          this.fetchCountsAndTrend(query);
        },
        error: (err: any) => {
          console.error('AlarmIdList failed — falling back to hardcoded sets', err);
          // Proceed anyway so the dashboard isn't blank
          this.fetchCountsAndTrend(query);
        },
      });
  }

  /**
   * Step 2 — fetch ALARM_COUNT and TREND_ALARMS in parallel.
   * Both use the now-seeded category sets from initAlarmCategories().
   */
  private fetchCountsAndTrend(query: any): void {
    const countPayload = {
      site:     query.site_ids,
      fromdate: query.from_date,
      todate:   query.to_date,
      type:     'ALARM_COUNT',
      search:   query.search

    };

    const trendPayload = {
      site:     query.site_ids,
      fromdate: query.from_date,
      todate:   query.to_date,
      type:     'TREND_ALARMS',
      search:   query.search

    };

    forkJoin({
      counts: this.alarmApi.GetAlarmsCounts_Critical(countPayload),
      trend:  this.alarmApi.GetAlarmsCounts_Critical(trendPayload),
    })
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: ({ counts, trend }: any) => {
        // ALARM_COUNT → CountRow[] → stat cards, pie, device bar, hbars
        this.countRows   = counts.Data.list as CountRow[];

        // TREND_ALARMS → AlarmTimeRow[] → trend line chart
        // The trend API returns rows with alarm_time timestamps — kept separate
        this.trendRows   = trend.Data.list as AlarmTimeRow[];

        this.applyCountData();
        this.buildTrendChart();
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        console.error('Counts/Trend fetch failed', err);
        this.cdr.markForCheck();
      },
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Data application helpers
  // ─────────────────────────────────────────────────────────────────────────

  /** Applies countRows → all stat cards, device stats, pie, hbars */
  private applyCountData(): void {
    this.statCards       = buildStatCardsFromCountRows(this.countRows);
    this.deviceStats     = buildDeviceStatsFromCountRows(this.countRows);
    this.deviceColourMap = new Map(this.deviceStats.map(d => [d.name, d.color]));
    this.alarmTypeBars   = buildTypeBarsFromCountRows(this.countRows);
    this.buildCountCharts();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Chart builders
  // ─────────────────────────────────────────────────────────────────────────

  /** Trend line — uses trendRows (AlarmTimeRow[] with alarm_time stamps) */
private async buildTrendChart(): Promise<void> {
  // Guard: if API returned an object (e.g. {GRID,DG,OTHER}) instead of an
  // array, trendRows won't be iterable — log and bail gracefully.
  if (!Array.isArray(this.trendRows)) {
    console.warn('buildTrendChart: trendRows is not an array', this.trendRows);
    return;
  }
 
  // For small datasets (< 50k records), use synchronous function
  if (this.trendRows.length < 50000) {
    const result = buildTrendChartFromTimeRows(this.trendRows, this.fromtime, this.totime);
    this.trendChartOptions = result;
    this.cdr.markForCheck();
    return;
  }
 
  // For large datasets (>= 50k records), use progressive loading
  this.isTrendChartLoading = true;
  this.trendChartLoadingProgress = 0;
  this.cdr.markForCheck();
 
  try {
    const result = await buildTrendChartFromTimeRowsProgressive(
      this.trendRows,
      this.fromtime,
      this.totime,
      {
        batchSize: 10000,    // Process 10k records at a time
        delayMs: 20,         // 20ms delay between batches
        onBatchComplete: (processed, total) => {
          // Update progress
          this.trendChartLoadingProgress = Math.round((processed / total) * 100);
          console.log(`Trend chart: loaded ${processed}/${total} records (${this.trendChartLoadingProgress}%)`);
          this.cdr.markForCheck();
        }
      }
    );
 
    this.trendChartOptions = result;
    console.log('Trend chart loaded successfully with', this.trendRows.length, 'records');
  } catch (error) {
    console.error('Error loading trend chart:', error);
  } finally {
    this.isTrendChartLoading = false;
    this.trendChartLoadingProgress = 100;
    this.cdr.markForCheck();
  }
}
 

  /** Pie + device bar — use countRows */
  private buildCountCharts(): void {
    // Donut
    const pieSlices = buildPieDataFromCountRows(this.countRows, this.pieCategories);
    this.pieChartOptions = {
      series:      pieSlices.map(s => s.value),
      chart:       { type: 'donut', height: '100%', width: '100%' },
      labels:      pieSlices.map(s => s.label),
      colors:      pieSlices.map(s => s.color),
      legend:      { show: true, position: 'right', fontSize: '12px', markers: { width: 10, height: 10, radius: 50 }, itemMargin: { vertical: 4 } },
      plotOptions: { pie: { donut: { size: '65%' } } },
      dataLabels:  { enabled: false },
      stroke:      { width: 2, colors: ['#ffffff'] },
      tooltip:     { y: { formatter: (v: number) => v + ' alarms' } },
    };

    // Device bar
    this.deviceChartOptions = {
      series:      [{ name: 'Alarms', data: this.deviceStats.map(d => d.count) }],
      chart:       { type: 'bar', height: '100%', width: '100%', toolbar: { show: false } },
      xaxis:       { categories: this.deviceStats.map(d => d.name), axisBorder: { show: false }, labels: { style: { fontSize: '11px', colors: '#64748b' } } },
      yaxis:       { labels: { style: { fontSize: '10px', colors: '#64748b' } } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '70%', distributed: true } },
      colors:      this.deviceStats.map(d => d.color),
      grid:        { borderColor: '#e2e8f0', strokeDashArray: 3 },
      dataLabels:  { enabled: false },
      legend:      { show: false },
      tooltip:     { y: { formatter: (v: number) => v + ' alarms' } },
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Template actions
  // ─────────────────────────────────────────────────────────────────────────

  setHbarFilter(f: 'all' | 'grid' | 'dg' | 'others'): void {
    this.hbarFilter = f;
    this.cdr.markForCheck();
  }

  setTableFilter(f: 'all' | 'high' | 'med' | 'low'): void {
    this.tableFilter      = f;
    this.tableCurrentPage = 1;
    this.cdr.markForCheck();
  }

  getDeviceBadgeClass(device: string): string {
    const key = device.toLowerCase();
    return ['meter', 'dic', 'dl'].includes(key)
      ? `aoc-dev-badge--${key}`
      : 'aoc-dev-badge--dynamic';
  }

  getDeviceBadgeStyle(device: string): Record<string, string> {
    if (['meter', 'dic', 'dl'].includes(device.toLowerCase())) return {};
    const color = this.deviceColourMap.get(device) ?? '#6b7280';
    return { background: `${color}1a`, color };
  }

  getAlarmValue(alarm: AlarmTimeRow, key: string): any {
    return (alarm as any)[key] ?? '—';
  }

switchTab(ActionType = '', API_FOCUS: string = '',Title:string = ''): void {

  if (ActionType === 'scroll') {

    const element = document.querySelector(
      `[api_focus="${API_FOCUS}"]`
    );

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }

  } else if (ActionType === 'table') {

  // Open Bootstrap modal first
  // const modalElement = document.getElementById('ModalTable');

  // if (modalElement) {
  //   const modal = new bootstrap.Modal(modalElement);
  //   modal.show();
  // }

  const payload = {
    site: this.currentQuery.site_ids,
    fromDate: this.currentQuery.from_date,
    toDate: this.currentQuery.to_date,
    type: 'ALARM_SUMMARY_COUNT_LIST',
    alarm_id: API_FOCUS
  };
  
  this.modalService.initFilters(
    payload,
    'AlarmSummaryDashboard',
    this.destroy$,
    Title
  );
}
  else{
    return
  }

}

  // ─────────────────────────────────────────────────────────────────────────
  // Critical alarms pagination
  // ─────────────────────────────────────────────────────────────────────────

  criticalCurrentPage = 1;
  criticalTotalCount  = 0;
  isCriticalLoading   = false;
  criticalError       = '';

  get deviceTypes(): string[] {
    return [...new Set(this.pagedCritical.map(a => a.device))].sort();
  }

  get filteredCritical(): CriticalAlarm[] {
    return this.pagedCritical.filter(a => {
      const sevOk    = this.criticalSevFilter === 'all' || a.priority === this.criticalSevFilter;
      const deviceOk = !this.criticalDeviceFilter || a.device === this.criticalDeviceFilter;
      return sevOk && deviceOk;
    });
  }

  get p1Count(): number { return this.pagedCritical.filter(a => a.priority === 'p1').length; }
  get p2Count(): number { return this.pagedCritical.filter(a => a.priority === 'p2').length; }

  get criticalTotalPages(): number {
    return Math.max(1, Math.ceil(this.criticalTotalCount / this.criticalPageSize));
  }

  get criticalPageNumbers(): number[] {
    return buildPageNumbers(this.criticalCurrentPage, this.criticalTotalPages);
  }

  get criticalPageStart(): number {
    return (this.criticalCurrentPage - 1) * this.criticalPageSize + 1;
  }

  get criticalPageEnd(): number {
    return Math.min(this.criticalCurrentPage * this.criticalPageSize, this.criticalTotalCount);
  }

  toggleCritical(): void { this.criticalOpen = !this.criticalOpen; }

  onCriticalFilterChange(): void { this.cdr.markForCheck(); }

  setCriticalSevFilter(f: 'all' | 'p1' | 'p2'): void {
    this.criticalSevFilter = f;
    this.cdr.markForCheck();
  }

  setCriticalPage(page: number): void {
    const clamped = Math.min(Math.max(1, page), this.criticalTotalPages);
    if (clamped === this.criticalCurrentPage) return;
    this.criticalCurrentPage = clamped;
    this.criticalService.goToPage(clamped, this.destroy$);
  }

  private fetchCriticalAlarms(query: any): void {
    this.criticalCurrentPage  = 1;
    this.criticalSevFilter    = 'all';
    this.criticalDeviceFilter = '';

    this.criticalService.initFilters(
      {
        fromDate: query.from_date ?? '',
        toDate:   query.to_date   ?? '',
        site:     query.site_ids,
        search:   query.search
      },
      this.criticalPageSize,
      this.destroy$,
    );
  }
  /** Resets all derived UI data to zero/empty — called on reset and on empty API response */
private resetDashboard(): void {
  this.countRows       = [];
  this.trendRows       = [];
  this.statCards       = [];
  this.deviceStats     = [];
  this.alarmTypeBars   = [];
  this.criticalHeaders = [];
  this.deviceColourMap = new Map();
  this.criticalService.hardReset();
  // Reset chart options to empty but valid shapes so apx-chart doesn't throw
  this.trendChartOptions = {
    series: [
      { name: 'Grid',   data: [] },
      { name: 'DG',     data: [] },
      { name: 'Others', data: [] },
    ],
    chart: { type: 'line', height: '100%', width: '100%' },
  };

  this.pieChartOptions = {
    series: [],
    chart:  { type: 'donut', height: '100%', width: '100%' },
    labels: [],
    colors: [],
  };

  this.deviceChartOptions = {
    series: [{ name: 'Alarms', data: [] }],
    chart:  { type: 'bar', height: '100%', width: '100%' },
    xaxis:  { categories: [] },
  };

  // Reset pagination + filters
  this.hbarFilter           = 'all';
  this.criticalCurrentPage  = 1;
  this.criticalTotalCount   = 0;
  this.criticalSevFilter    = 'all';
  this.criticalDeviceFilter = '';
  this.criticalError        = '';
  this.isCriticalLoading    = false;
  this.fromtime             = '';
  this.totime               = '';

  this.cdr.markForCheck();
}

/** Called by log-select reset button */
handlereset(_: boolean = true): void {
  this.showReport = false;
  this.resetDashboard();
}
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared pagination helper
// ─────────────────────────────────────────────────────────────────────────────
function buildPageNumbers(current: number, total: number): number[] {
  const pages = new Set<number>([1, total]);
  for (let i = Math.max(2, current - 2); i <= Math.min(total - 1, current + 2); i++) {
    pages.add(i);
  }
  return [...pages].sort((a, b) => a - b);
}